import React from 'react';
import { X, Droplets } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface WaterRegZoneProperties {
  id: number;
  name: string;
  zone_type: string;
  state: string;
  requirements: string | null;
  geologic_unit: string | null;
  source: string | null;
  metadata: Record<string, unknown> | null;
}

interface Props {
  zone: WaterRegZoneProperties;
  onClose: () => void;
}

const TYPE_INFO: Record<string, { label: string; description: string }> = {
  gcd: {
    label: 'Groundwater Conservation District',
    description: 'Texas district with authority to regulate groundwater production through permits, spacing rules, production limits, and fees. Created under Texas Water Code Chapter 36. Rules vary significantly by district.',
  },
  ama: {
    label: 'Active Management Area',
    description: 'Arizona zone where ADWR regulates groundwater withdrawals. New wells require a permit. Large users must demonstrate 100-year assured water supply.',
  },
  ina: {
    label: 'Irrigation Non-Expansion Area',
    description: 'Arizona zone prohibiting expansion of irrigated acreage. Existing irrigation rights preserved, but new agricultural irrigation banned.',
  },
  declared_basin: {
    label: 'Declared Underground Water Basin',
    description: 'New Mexico basin where the State Engineer has asserted jurisdiction over groundwater. All new appropriations require an OSE permit.',
  },
  regulation: {
    label: 'Regulated Groundwater Area',
    description: 'New Mexico area under State Engineer regulation. Groundwater appropriation requires an OSE permit. Existing rights are administered under priority system.',
  },
  closure: {
    label: 'Groundwater Closure Area',
    description: 'New Mexico area closed to new groundwater appropriations. No new permits issued. Water rights must be acquired through transfer of existing rights.',
  },
};

const TYPE_COLORS: Record<string, string> = {
  gcd: '#0284c7',
  ama: '#d97706',
  ina: '#c48a5c',
  declared_basin: '#b85c5c',
  regulation: '#16a34a',
  closure: '#dc2626',
};

const BTM_IMPACT: Record<string, { severity: string; detail: string }> = {
  gcd: {
    severity: 'Low–Moderate',
    detail: 'BTM air-cooled auxiliary needs ~1,000 GPD (~1 AFY, zero evaporative makeup). Most GCDs process permits for this volume without difficulty, but rules vary by district. Commercial/industrial wells are not exempt (§36.117 exemptions cover domestic/livestock only). Contact the district for spacing rules, production limits, and fees. Typical permit: 60-120 days; longer if contested. Well construction must comply with TDLR standards (licensed driller required).',
  },
  ama: {
    severity: 'Moderate',
    detail: 'ADWR permit required. Must demonstrate adequate water supply. For BTM volumes (~1 AFY), a Type 1 Non-Irrigation Grandfathered Right or a new permit application is typical. Timeline: 3-6 months.',
  },
  ina: {
    severity: 'Low',
    detail: 'INA restrictions apply to irrigation expansion only. Industrial/commercial water use (including BTM cooling) is not restricted by INA designation. Standard ADWR rules apply.',
  },
  declared_basin: {
    severity: 'Moderate',
    detail: 'OSE permit required for any new groundwater appropriation. NM prior appropriation system — junior rights can be curtailed during shortage. BTM volumes are small but permitting takes 6-12 months.',
  },
  regulation: {
    severity: 'Moderate',
    detail: 'OSE permit required. Metering and reporting obligations. BTM volumes (~1 AFY) are well within typical permit ranges, but processing times are 6-12 months.',
  },
  closure: {
    severity: 'High',
    detail: 'No new groundwater appropriations permitted. Must acquire existing water rights through purchase/lease/transfer. Water right transfers require OSE approval (6-18 months). Consider trucked water as interim supply.',
  },
};

export const WaterRegZoneDetail: React.FC<Props> = ({ zone, onClose }) => {
  const { theme } = useTheme();
  const info = TYPE_INFO[zone.zone_type] || { label: zone.zone_type, description: 'Water regulatory zone.' };
  const color = TYPE_COLORS[zone.zone_type] || '#6A7E8E';
  const impact = BTM_IMPACT[zone.zone_type];

  let meta: Record<string, unknown> | null = null;
  try { meta = typeof zone.metadata === 'string' ? JSON.parse(zone.metadata) : zone.metadata; } catch { /* */ }

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Droplets size={18} color={color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Water Regulatory Zone</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Type badge */}
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: color + '15', border: `1px solid ${color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '4px' }}>
            {info.label}
          </div>
          {zone.name && zone.name !== info.label && (
            <div style={{ fontSize: '13px', fontWeight: 600, color: theme.text, marginBottom: '6px' }}>
              {zone.name}
            </div>
          )}
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            {info.description}
          </div>
        </div>

        {/* Details */}
        <Section title="Zone Details">
          <Field label="State" value={zone.state === 'TX' ? 'Texas' : zone.state === 'AZ' ? 'Arizona' : zone.state === 'NM' ? 'New Mexico' : zone.state} />
          <Field label="Zone Type" value={info.label} />
          {zone.geologic_unit && <Field label="Geologic Unit" value={zone.geologic_unit} />}
          {zone.requirements && <Field label="Requirements" value={zone.requirements} />}
          {meta?.established && <Field label="Established" value={String(meta.established)} />}
          {meta?.area_sq_miles && <Field label="Area" value={`${Number(meta.area_sq_miles).toLocaleString()} sq mi`} />}
        </Section>

        {/* BTM Impact */}
        {impact && (
          <Section title="BTM Development Impact">
            <div style={{
              padding: '10px', borderRadius: '6px', marginBottom: '8px',
              background: impact.severity === 'High' ? 'rgba(220,38,38,0.1)' : impact.severity === 'Moderate' ? 'rgba(217,119,6,0.1)' : 'rgba(22,163,74,0.1)',
              border: `1px solid ${impact.severity === 'High' ? 'rgba(220,38,38,0.2)' : impact.severity === 'Moderate' ? 'rgba(217,119,6,0.2)' : 'rgba(22,163,74,0.2)'}`,
            }}>
              <div style={{
                fontSize: '12px', fontWeight: 700, marginBottom: '6px',
                color: impact.severity === 'High' ? '#dc2626' : impact.severity === 'Moderate' ? '#d97706' : '#16a34a',
              }}>
                Permitting Complexity: {impact.severity}
              </div>
              <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.6 }}>
                {impact.detail}
              </div>
            </div>
          </Section>
        )}

        {/* Source */}
        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            <div>{zone.source === 'twdb' ? 'Texas Water Development Board (TWDB)' : zone.source === 'adwr' ? 'Arizona Dept. of Water Resources (ADWR)' : zone.source === 'nm_ose' ? 'New Mexico Office of the State Engineer (OSE)' : zone.source || 'State water agency'}</div>
          </div>
        </Section>
      </div>
    </div>
  );
};
